import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { EmptyState } from '../components/EmptyState';
import { BulkOwnerBar } from '../components/tenders/BulkOwnerBar';
import { Briefcase, Plus, MagnifyingGlass, Warning, CheckCircle, FileText, Clock, CurrencyInr, Buildings, ArrowSquareOut } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import useAuthStore from '../store/authStore';
import { useTenantPath } from '../lib/tenantPath';

import { notify } from '../lib/notify';
import { InlineSkeleton } from '../components/Skeletons';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SOURCES = ['GeM', 'CPPP/eProcure', 'State portal', 'Private hospital', 'Offline visit', 'Other'];

const STATUS_TONE = {
  draft: 'bg-gray-100 text-gray-700',
  feasibility_pending: 'bg-amber-100 text-amber-800',
  go_ahead: 'bg-teal-100 text-teal-800',
  no_go: 'bg-rose-100 text-rose-700',
  submitted: 'bg-blue-100 text-blue-800',
  tech_qualified: 'bg-blue-100 text-blue-800',
  tech_disqualified: 'bg-rose-100 text-rose-700',
  awarded: 'bg-emerald-100 text-emerald-800',
  not_selected: 'bg-rose-100 text-rose-700',
  withdrawn: 'bg-gray-100 text-gray-700',
};

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

/**
 * Tenders — Feb 2026 (iter89) Phase 1.
 * Portal-driven tender lifecycle. Separate module from Leads; on Award the
 * backend auto-creates a linked Lead in "won" status so the existing
 * Quote → PI → Payment flow can take over.
 */
export default function Tenders() {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const tp = useTenantPath();
  const [tenders, setTenders] = useState([]);
  const [summary, setSummary] = useState(null);
  const [deadlines, setDeadlines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [ownerFilter, setOwnerFilter] = useState('');
  const [tenderExecs, setTenderExecs] = useState([]);
  const [selected, setSelected] = useState([]);
  const canBulkAssign = ['National Sales Manager', 'Vice President', 'President'].includes(user?.role);
  const toggleSel = (id) => setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const [showCreate, setShowCreate] = useState(false);

  const canCreate = ['Tender Executive', 'Regional Sales Manager', 'National Sales Manager',
                     'Vice President', 'President'].includes(user?.role);

  const load = async () => {
    setLoading(true);
    try {
      const [t, s, d] = await Promise.all([
        axios.get(`${API}/tenders`, { params: { ...(statusFilter ? { status: statusFilter } : {}), ...(ownerFilter ? { owner: ownerFilter } : {}) } }),
        axios.get(`${API}/tenders/summary`),
        axios.get(`${API}/tenders/upcoming-deadlines`, { params: { days: 7 } }),
      ]);
      setTenders(t.data || []);
      setSummary(s.data);
      setDeadlines(d.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter, ownerFilter]);
  useEffect(() => {
    axios.get(`${API}/tenders/tender-executives`).then((r) => setTenderExecs(r.data.items || [])).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return tenders;
    return tenders.filter((t) =>
      (t.buyer_name || '').toLowerCase().includes(q) ||
      (t.tender_number || '').toLowerCase().includes(q) ||
      (t.tender_ref_id || '').toLowerCase().includes(q)
    );
  }, [tenders, search]);

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="tenders-page">
      <div className="mb-6 flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl bg-teal-50 flex items-center justify-center text-teal-600">
          <Briefcase size={28} weight="bold" />
        </div>
        <div className="flex-1">
          <h1 className="app-page-title" style={{ fontFamily: 'Outfit' }}>
            Tenders
          </h1>
          <p className="text-[#6D8E87]">Portal-driven tender pipeline: discovery → feasibility → submission → award.</p>
        </div>
        {canCreate && (
          <div className="flex gap-2">
            <label
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-[#00a69c] text-[#00a69c] text-sm font-semibold hover:bg-[#E2F3F0] cursor-pointer"
              title="Upload a CSV with one tender per row"
              data-testid="tender-csv-upload-btn"
            >
              <FileText size={14} weight="bold" /> Upload CSV
              <input
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={async (e) => {
                  const f = e.target.files?.[0];
                  if (!f) return;
                  const fd = new FormData();
                  fd.append('file', f);
                  try {
                    const r = await axios.post(`${API}/tenders/import-csv`, fd);
                    // iter132.9 — Surface skipped rows AND per-row errors in
                    // the success alert so a partial-success upload isn't a
                    // silent data-quality trap.
                    const errs = (r.data.errors || []);
                    const errTxt = errs.length ? `\n\nRow issues (${errs.length}):\n• ${errs.slice(0, 10).join('\n• ')}${errs.length > 10 ? `\n…and ${errs.length - 10} more` : ''}` : '';
                    notify(`${r.data.message}${errTxt}`);
                    e.target.value = '';
                    load();
                  } catch (er) {
                    // iter132.9 — Surface the real backend error instead of
                    // a generic "Upload failed". `detail` can be a FastAPI
                    // 422 array; stringify defensively so the user always
                    // sees something actionable.
                    const detail = er.response?.data?.detail;
                    const msg = Array.isArray(detail)
                      ? detail.map((d) => d.msg || JSON.stringify(d)).join('; ')
                      : (typeof detail === 'string' ? detail : (er.message || 'Upload failed'));
                    notify(`CSV upload failed:\n\n${msg}`);
                    e.target.value = '';
                  }
                }}
              />
            </label>
            <a
              href={`${API}/tenders/bulk/csv-template`}
              onClick={async (e) => {
                e.preventDefault();
                const r = await axios.get(`${API}/tenders/bulk/csv-template`);
                const header = r.data.columns.join(',');
                const example = r.data.columns.map((c) => r.data.example_row[c] ?? '').join(',');
                const blob = new Blob([header + '\n' + example + '\n'], { type: 'text/csv' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url; a.download = 'tender-template.csv'; a.click();
                URL.revokeObjectURL(url);
              }}
              className="inline-flex items-center gap-1 px-2 py-2 text-xs text-[#6D8E87] hover:text-[#00a69c]"
              data-testid="tender-csv-template-link"
            >Template ↓</a>
            <button
              type="button"
              onClick={() => setShowCreate(true)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#00a69c] text-white text-sm font-semibold hover:bg-[#00bfb3]"
              data-testid="tender-create-btn"
            >
              <Plus size={16} weight="bold" /> New Tender
            </button>
          </div>
        )}
      </div>

      {/* KPI Tiles */}
      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-6" data-testid="tender-tiles">
          <Tile icon={Clock} label="In Feasibility" value={summary.open_in_feasibility} testId="tile-in-feas" />
          <Tile icon={FileText} label="Submitted MTD" value={summary.submitted_this_month} testId="tile-submitted" />
          <Tile icon={CurrencyInr} label="EMD in hand" value={inr(summary.emd_in_hand)} testId="tile-emd" />
          <Tile icon={CheckCircle} label="Win rate" value={`${summary.bid_win_rate_pct}%`} testId="tile-winrate" />
          <Tile icon={Buildings} label="Awarded MTD" value={inr(summary.awarded_value_mtd)} testId="tile-awarded-mtd" />
        </div>
      )}

      {/* Approaching deadlines banner */}
      {deadlines.length > 0 && (
        <div className="mb-6 medical-card p-4 border-l-4 border-amber-400" data-testid="tender-deadlines-banner">
          <div className="flex items-center gap-2 mb-2 text-amber-800 font-semibold">
            <Warning size={18} weight="fill" />
            {deadlines.length} tender{deadlines.length === 1 ? '' : 's'} closing within 7 days
          </div>
          <div className="flex flex-wrap gap-2">
            {deadlines.slice(0, 6).map((d) => (
              <button
                key={d.id}
                onClick={() => navigate(tp(`/tenders/${d.id}`))}
                className="text-xs px-2 py-1 rounded-md bg-amber-50 hover:bg-amber-100 border border-amber-200 text-amber-900"
                data-testid={`tender-deadline-${d.id}`}
              >
                <span className="font-mono">{d.tender_number}</span> · {d.buyer_name} · <span className="font-semibold">{d.submission_deadline}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative max-w-sm flex-1">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6D8E87]" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search buyer / tender number …"
            className="w-full pl-9 pr-3 py-2 border border-[#D5E8E4] rounded-lg text-sm bg-white"
            data-testid="tender-search"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 border border-[#D5E8E4] rounded-lg text-sm bg-white"
          data-testid="tender-status-filter"
        >
          <option value="">All statuses</option>
          {Object.keys(STATUS_TONE).map((s) => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
        </select>
        <select
          value={ownerFilter}
          onChange={(e) => setOwnerFilter(e.target.value)}
          className="px-3 py-2 border border-[#D5E8E4] rounded-lg text-sm bg-white"
          data-testid="tender-owner-filter"
        >
          <option value="">All owners</option>
          {user?.role === 'Tender Executive' && <option value="me">Mine</option>}
          <option value="unassigned">Unassigned</option>
          {tenderExecs.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
      </div>

      {canBulkAssign && selected.length > 0 && (
        <BulkOwnerBar selectedIds={selected} tenderExecs={tenderExecs} onDone={load} onClear={() => setSelected([])} />
      )}

      {/* List */}
      <div className="medical-card overflow-hidden" data-testid="tender-list">
        {loading ? (
          <InlineSkeleton />
        ) : filtered.length === 0 ? (
          <EmptyState title="No tenders here" hint="Click New Tender to add one, or change the filters above." testid="tender-list-empty" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F4F9F7] text-[10px] uppercase tracking-widest text-[#6D8E87]">
                <tr>
                  {canBulkAssign && (
                    <th className="px-3 py-3 w-8">
                      <input
                        type="checkbox"
                        checked={filtered.length > 0 && filtered.every((t) => selected.includes(t.id))}
                        onChange={(e) => setSelected(e.target.checked ? filtered.map((t) => t.id) : [])}
                        className="accent-[#00a69c]"
                        data-testid="tender-select-all"
                      />
                    </th>
                  )}
                  <th className="text-left px-4 py-3">Tender #</th>
                  <th className="text-left px-4 py-3">Buyer</th>
                  <th className="text-left px-4 py-3">Source</th>
                  <th className="text-right px-4 py-3">Est. Value</th>
                  <th className="text-left px-4 py-3">Deadline</th>
                  <th className="text-left px-4 py-3">Owner</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-right px-4 py-3">Open</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((t) => (
                  <tr
                    key={t.id}
                    className="border-t border-[#E2ECEA] hover:bg-[#FAFDFC] cursor-pointer"
                    onClick={() => navigate(tp(`/tenders/${t.id}`))}
                    data-testid={`tender-row-${t.id}`}
                  >
                    {canBulkAssign && (
                      <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selected.includes(t.id)}
                          onChange={() => toggleSel(t.id)}
                          className="accent-[#00a69c]"
                          data-testid={`tender-select-${t.id}`}
                        />
                      </td>
                    )}
                    <td className="px-4 py-3 font-mono text-[#052A23]">{t.tender_number}</td>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-[#052A23]">{t.buyer_name}</div>
                      <div className="text-[11px] text-[#6D8E87]">{t.tender_ref_id || ''}</div>
                    </td>
                    <td className="px-4 py-3 text-[#3B5A54]">{t.source}</td>
                    <td className="px-4 py-3 text-right font-mono text-[#00a69c]">{inr(t.estimated_value)}</td>
                    <td className="px-4 py-3 text-[#3B5A54]">{t.submission_deadline}</td>
                    <td className="px-4 py-3 text-[#3B5A54]" data-testid={`tender-row-owner-${t.id}`}>
                      {t.owner_te_name || <span className="text-[#9BB5AF] italic">unassigned</span>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase ${STATUS_TONE[t.status] || 'bg-gray-100'}`}>
                        {(t.status || '').replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <ArrowSquareOut size={14} className="inline text-[#00a69c]" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showCreate && (
        <CreateTenderModal
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }}
        />
      )}
    </div>
  );
}

const Tile = ({ icon: Icon, label, value, testId }) => (
  <div className="medical-card p-4" data-testid={testId}>
    <div className="flex items-center gap-2 text-[#6D8E87] text-[11px] uppercase tracking-wider mb-1">
      <Icon size={14} weight="bold" /> {label}
    </div>
    <div className="text-xl font-bold text-[#052A23]">{value}</div>
  </div>
);

// iter93 — mandatory document categories in the New Tender form.
const REQUIRED_DOCS = [
  { key: 'tender_doc', label: 'Tender Document', hint: 'Full tender / RFP file from the portal' },
  { key: 'emd_page', label: 'EMD Page', hint: 'Page where EMD amount & mode are specified' },
  { key: 'participation_criteria', label: 'Participation Criteria', hint: 'Eligibility, turnover, past-supply clauses' },
  { key: 'boq_sheet', label: 'BOQ Sheet', hint: 'Bill of Quantities (line items + specs)' },
];

function CreateTenderModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    source: 'GeM',
    buyer_name: '',
    tender_ref_id: '',
    region: '',
    state: '',
    source_url: '',
    source_notes: '',
    publish_date: '',
    submission_deadline: '',
    opening_date: '',
    estimated_value: 0,
    emd: { required: false, amount: 0, mode: 'Bank Guarantee', bank: '', ref_number: '', validity_date: '' },
    pbg_required: false,
    pbg_amount: 0,
    reverse_auction: false,
    emd_exemption: false,
    rate_contract: false,
  });
  const [saving, setSaving] = useState(false);
  const [salesExecs, setSalesExecs] = useState([]);
  const [pairedSe, setPairedSe] = useState('');
  const [stateToRegion, setStateToRegion] = useState({});
  const [regionsList, setRegionsList] = useState(['North', 'South', 'East', 'West']);
  // { tender_doc: {name, url} | null, emd_page: {...} | null, ... }
  const [docs, setDocs] = useState({ tender_doc: null, emd_page: null, participation_criteria: null, boq_sheet: null });
  const [uploadingKey, setUploadingKey] = useState(null);
  // iter99 — AI auto-fill from tender document.
  //   aiSuggested: Set of form field keys populated by the LLM (drives the ✨ pill next to the label).
  //   aiStatus: 'idle' | 'extracting' | 'done' | 'failed'   (shown as a small toast under the doc slot)
  //   aiStep2Hints: LLM-extracted step 2 fields, stashed here and reused when TenderDetail opens Participation.
  const [aiSuggested, setAiSuggested] = useState(new Set());
  const [aiStatus, setAiStatus] = useState('idle');
  const [aiReason, setAiReason] = useState('');  // iter99c — surface the failure reason
  const [aiStep2Hints, setAiStep2Hints] = useState(null);
  // iter107 — feasibility rows returned by the async job. Editable in place
  // by the TE; wrong-verdict flag button pushes to backend audit collection.
  const [feasibilityRows, setFeasibilityRows] = useState([]);

  useEffect(() => {
    // Fetch SE roster (region + state on each row) and region/state map in parallel
    Promise.all([
      axios.get(`${API}/tenders/sales-executives`).catch(() => ({ data: { users: [] } })),
      axios.get(`${API}/regions`).catch(() => ({ data: {} })),
    ]).then(([seRes, regRes]) => {
      setSalesExecs(seRes.data?.users || []);
      setStateToRegion(regRes.data?.state_to_region || {});
      if (Array.isArray(regRes.data?.regions) && regRes.data.regions.length) setRegionsList(regRes.data.regions);
    });
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setEmd = (k, v) => setForm((f) => ({ ...f, emd: { ...f.emd, [k]: v } }));

  // Cascade: states for the currently-selected region
  const statesForRegion = useMemo(() => {
    if (!form.region) return [];
    return Object.entries(stateToRegion)
      .filter(([, r]) => r === form.region)
      .map(([s]) => s)
      .sort();
  }, [form.region, stateToRegion]);

  // Cascade: SEs whose region+state match the tender's region+state
  const filteredSEs = useMemo(() => {
    if (!form.region || !form.state) return [];
    return salesExecs.filter((u) => {
      if (u.role && u.role !== 'Sales Executive') return false;
      if (u.region !== form.region) return false;
      // SE may not have granular state — fall back to region-match only when state is blank.
      return !u.state || u.state === form.state;
    });
  }, [salesExecs, form.region, form.state]);

  const onRegionChange = (val) => {
    setForm((f) => ({ ...f, region: val, state: '' }));
    setPairedSe('');
  };
  const onStateChange = (val) => {
    setForm((f) => ({ ...f, state: val }));
    setPairedSe('');
  };

  // iter102 — poll the async extract job every 3s until done/failed. Max
  // 60 attempts (~3 min) to protect against a stuck job holding the UI open.
  const pollExtractJob = (jobId, tries = 0) => {
    if (tries > 60) {
      setAiReason('Extraction is taking too long. Please fill the form manually.');
      setAiStatus('failed');
      return;
    }
    axios.get(`${API}/tenders/extract-status/${jobId}`).then((res) => {
      const { status, fields, auto_docs, reason } = res.data || {};
      if (status === 'done' && fields) {
        applyAiFields(fields);
        const autoDocs = auto_docs || {};
        const gotSlices = Object.keys(autoDocs).length > 0;
        if (gotSlices) {
          setDocs((d) => {
            const next = { ...d };
            for (const [slot, info] of Object.entries(autoDocs)) {
              if (!next[slot]) {
                next[slot] = { name: info.filename, url: info.file_url, size: 0, ai: true, pages: info.pages || null };
              }
            }
            return next;
          });
        }
        // iter107 — store feasibility rows for the review panel.
        setFeasibilityRows(res.data?.feasibility_check || []);
        setAiStatus(gotSlices ? 'done' : 'done-no-slices');
      } else if (status === 'failed') {
        setAiReason(reason || '');
        setAiStatus('failed');
      } else {
        setTimeout(() => pollExtractJob(jobId, tries + 1), 3000);
      }
    }).catch((err) => {
      setAiReason(err?.message || 'network error');
      setAiStatus('failed');
    });
  };

  const handleFile = async (key, file) => {
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) { notify('File too large (max 25 MB)'); return; }
    setUploadingKey(key);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/tenders/upload-doc`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setDocs((d) => ({ ...d, [key]: { name: r.data.filename, url: r.data.file_url, size: r.data.size_bytes } }));

      // iter99 — kick off AI extraction ONLY for the main Tender Document.
      // Runs in parallel; never blocks the user; fields it can't find stay blank.
      if (key === 'tender_doc' && (file.name || '').toLowerCase().endsWith('.pdf')) {
        setAiStatus('extracting');
        const efd = new FormData();
        efd.append('file', file);
        // iter102 — async job: POST returns {job_id}, we poll /extract-status/{id}
        // every 3s until done/failed. Avoids Cloudflare's ~100s proxy timeout
        // that used to hit on 90+ page govt tenders.
        axios.post(`${API}/tenders/extract-doc`, efd, {
          headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000,
        }).then((res) => {
          if (res.data?.ok && res.data.job_id) {
            pollExtractJob(res.data.job_id);
          } else {
            setAiReason(res.data?.reason || 'Could not queue extraction');
            setAiStatus('failed');
          }
        }).catch((err) => {
          setAiReason(err?.message || 'network error');
          setAiStatus('failed');
        });
      }
    } catch (e) {
      notify(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingKey(null);
    }
  };

  // iter99 — merge LLM extraction into form state.
  // Rule: NEVER overwrite fields the TE already typed. Only fill blanks.
  const applyAiFields = (ai) => {
    if (!ai || typeof ai !== 'object') return;
    const marked = new Set(aiSuggested);
    setForm((prev) => {
      const next = { ...prev };
      const fillIf = (k, v) => {
        if (v === null || v === undefined || v === '') return;
        const cur = next[k];
        if (cur === '' || cur === null || cur === undefined || cur === 0) {
          next[k] = v;
          marked.add(k);
        }
      };
      fillIf('buyer_name', ai.buyer_name);
      fillIf('tender_ref_id', ai.tender_ref_id);
      fillIf('publish_date', ai.publish_date);
      fillIf('submission_deadline', ai.submission_deadline);
      fillIf('opening_date', ai.opening_date);
      fillIf('estimated_value', ai.estimated_value);
      fillIf('source_url', ai.source_url);
      fillIf('source_notes', ai.source_notes);
      // Region/state need cascade validation — only fill when they align.
      if (ai.region && !next.region && ['North', 'South', 'East', 'West'].includes(ai.region)) {
        next.region = ai.region; marked.add('region');
      }
      if (ai.state && !next.state) {
        next.state = ai.state; marked.add('state');
      }
      // 3 booleans — only overwrite the default 'false' when doc explicitly says true.
      if (ai.reverse_auction === true) { next.reverse_auction = true; marked.add('reverse_auction'); }
      if (ai.emd_exemption === true) { next.emd_exemption = true; marked.add('emd_exemption'); }
      if (ai.rate_contract === true) { next.rate_contract = true; marked.add('rate_contract'); }
      // EMD block — only fill sub-fields that arrived from AI and are blank.
      if (ai.emd && typeof ai.emd === 'object') {
        const emd = { ...next.emd };
        if (ai.emd.required === true && !emd.required) { emd.required = true; marked.add('emd.required'); }
        if (ai.emd.amount && (!emd.amount || emd.amount === 0)) { emd.amount = Number(ai.emd.amount); marked.add('emd.amount'); }
        if (ai.emd.mode && !emd.mode) { emd.mode = ai.emd.mode; marked.add('emd.mode'); }
        if (ai.emd.bank && !emd.bank) { emd.bank = ai.emd.bank; marked.add('emd.bank'); }
        if (ai.emd.validity_date && !emd.validity_date) { emd.validity_date = ai.emd.validity_date; marked.add('emd.validity_date'); }
        next.emd = emd;
      }
      return next;
    });
    setAiSuggested(marked);
    // Stash step 2 hints for later — TenderDetail's ParticipationSection
    // picks these up via localStorage keyed by (soon-to-be) tender_ref_id.
    if (ai.step2 && ai.tender_ref_id) {
      try {
        localStorage.setItem(`tender_ai_step2_${ai.tender_ref_id}`, JSON.stringify(ai.step2));
      } catch { /* quota / disabled — ignore */ }
    }
  };

  const removeDoc = (key) => setDocs((d) => ({ ...d, [key]: null }));

  // iter101 — build a JWT-appended URL so <iframe> can view /api/files/... previews.
  const authedPreviewUrl = (url) => {
    if (!url) return '';
    const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
    const base = url.startsWith('http') ? url : `${API.replace(/\/api$/, '')}${url}`;
    return token ? `${base}${base.includes('?') ? '&' : '?'}auth=${encodeURIComponent(token)}` : base;
  };

  // iter101 — Track slots the TE has already flagged (prevents accidental duplicate reports).
  const [flaggedSlots, setFlaggedSlots] = useState({});

  const reportWrongSlice = async (slotKey) => {
    const slot = docs[slotKey];
    if (!slot) return;
    setFlaggedSlots((s) => ({ ...s, [slotKey]: 'reporting' }));
    try {
      await axios.post(`${API}/tenders/report-wrong-slice`, {
        slot: slotKey,
        ai_file_url: slot.url || '',
        ai_pages: slot.pages || null,
        tender_doc_url: docs.tender_doc?.url || '',
        note: '',
      });
      setFlaggedSlots((s) => ({ ...s, [slotKey]: 'reported' }));
      // Clear the AI-picked doc so TE is forced to upload the correct one manually.
      removeDoc(slotKey);
    } catch (e) {
      // Non-blocking — backend already swallows failures. Show a light hint.
      setFlaggedSlots((s) => ({ ...s, [slotKey]: 'error' }));
    }
  };

  const allDocsUploaded = REQUIRED_DOCS.every((d) => docs[d.key]);

  const submit = async () => {
    if (!form.buyer_name || !form.submission_deadline) { notify('Buyer and submission deadline required'); return; }
    if (!form.region) { notify('Please select a region'); return; }
    if (!form.state) { notify('Please select a state'); return; }
    if (!allDocsUploaded) { notify('Please upload all 4 mandatory documents before creating the tender'); return; }
    setSaving(true);
    try {
      const documents = REQUIRED_DOCS.map((d) => ({
        name: docs[d.key].name,
        url: docs[d.key].url,
        category: d.key,
      }));
      const payload = { ...form, paired_sales_executive_id: pairedSe || '', documents };
      await axios.post(`${API}/tenders`, payload);
      onCreated();
    } catch (e) {
      notify(e.response?.data?.detail || 'Create failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="tender-create-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] overflow-y-auto">
        <div className="p-6 border-b border-[#E2ECEA] flex items-center justify-between sticky top-0 bg-white z-10">
          <h2 className="text-xl font-bold text-[#052A23]" style={{ fontFamily: 'Outfit' }}>New Tender</h2>
          <button onClick={onClose} className="text-[#6D8E87] hover:text-[#052A23]" data-testid="tender-create-close">✕</button>
        </div>

        {/* iter99b — Tender Document upload lifted to the TOP so TEs upload
            first, then the AI auto-fills the fields below. The 3 supporting
            docs (EMD page, Participation criteria, BOQ) stay in their own
            block near the bottom. */}
        <div className="px-6 pt-5 pb-2">
          <div className={`rounded-xl border-2 border-dashed ${docs.tender_doc ? 'border-emerald-300 bg-emerald-50' : 'border-indigo-300 bg-indigo-50/40'} p-4 transition`}>
            <div className="flex items-start gap-3">
              <span className="text-2xl select-none leading-none">✨</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2">
                  <h3 className="font-bold text-[#052A23]">Upload Tender Document <span className="text-rose-600">*</span></h3>
                  <span className="text-[10px] uppercase tracking-wider text-indigo-700 font-bold">Start here</span>
                </div>
                <p className="text-xs text-[#3B5A54] mt-0.5">
                  Drop the full tender / RFP PDF here first. Our AI will read it and pre-fill the form below — you can review and edit before creating.
                </p>
                {docs.tender_doc && (
                  <p className="text-xs text-emerald-800 mt-1 truncate" data-testid="tender-doc-top-name">{docs.tender_doc.name}</p>
                )}
                {aiStatus !== 'idle' && (
                  <div
                    className={`mt-2 px-2.5 py-1.5 rounded text-[11px] flex items-center gap-1.5 ${
                      aiStatus === 'extracting' ? 'bg-white text-indigo-800' :
                      aiStatus === 'done' ? 'bg-white text-emerald-800' :
                      aiStatus === 'done-no-slices' ? 'bg-white text-amber-800' :
                      'bg-white text-amber-800'
                    }`}
                    data-testid="tender-ai-status"
                  >
                    {aiStatus === 'extracting' && (<><span className="animate-pulse">✨</span> Reading with AI… large tenders can take up to a minute. Keep filling other fields in the meantime.</>)}
                    {aiStatus === 'done' && (<><span>✅</span> AI has pre-filled the fields it detected and auto-attached the relevant EMD / Participation / BOQ pages sliced from this document. Please review before creating.</>)}
                    {aiStatus === 'done-no-slices' && (<><span>⚠️</span> AI pre-filled the fields but could not identify EMD / Participation Criteria / BOQ pages inside this PDF. Please upload those 3 documents manually below.</>)}
                    {aiStatus === 'failed' && (<><span>⚠️</span> Auto-fill unavailable — please fill the form manually.{aiReason && <span className="ml-1 text-[10px] opacity-70">({aiReason.slice(0, 60)})</span>}</>)}
                  </div>
                )}
              </div>
              <div className="shrink-0">
                {docs.tender_doc ? (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-emerald-700 font-semibold">Uploaded ✓</span>
                    <button type="button" onClick={() => { removeDoc('tender_doc'); setAiSuggested(new Set()); setAiStatus('idle'); }} className="text-xs px-2 py-1 rounded border border-rose-200 text-rose-600 hover:bg-rose-50" data-testid="tender-doc-top-remove">Remove</button>
                  </div>
                ) : (
                  <label className={`inline-flex items-center gap-1.5 text-xs px-4 py-2 rounded font-semibold cursor-pointer ${uploadingKey === 'tender_doc' ? 'bg-gray-200 text-gray-500' : 'bg-indigo-600 text-white hover:bg-indigo-700'}`} data-testid="tender-doc-top-upload">
                    {uploadingKey === 'tender_doc' ? 'Uploading…' : 'Choose PDF'}
                    <input type="file" className="hidden" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp,.xls,.xlsx" onChange={(e) => handleFile('tender_doc', e.target.files?.[0])} disabled={uploadingKey === 'tender_doc'} />
                  </label>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Discovery */}
        <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Source *"><select value={form.source} onChange={(e) => set('source', e.target.value)} data-testid="tender-source" className="input">{SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}</select></Field>
          <Field label="Buyer name *" aiHint={aiSuggested.has('buyer_name')}><input value={form.buyer_name} onChange={(e) => set('buyer_name', e.target.value)} className="input" data-testid="tender-buyer" /></Field>
          <Field label="Tender ref ID (portal)" aiHint={aiSuggested.has('tender_ref_id')}><input value={form.tender_ref_id} onChange={(e) => set('tender_ref_id', e.target.value)} className="input" data-testid="tender-ref-id" /></Field>
          <Field label="Region *" aiHint={aiSuggested.has('region')}>
            <select value={form.region} onChange={(e) => onRegionChange(e.target.value)} className="input" data-testid="tender-region">
              <option value="">— Select region —</option>
              {regionsList.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </Field>
          <Field label="State *" aiHint={aiSuggested.has('state')}>
            <select value={form.state} onChange={(e) => onStateChange(e.target.value)} disabled={!form.region} className="input" data-testid="tender-state">
              <option value="">{form.region ? '— Select state —' : '— Pick region first —'}</option>
              {statesForRegion.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Paired Sales Executive">
            <select value={pairedSe} onChange={(e) => setPairedSe(e.target.value)} disabled={!form.region || !form.state} className="input" data-testid="tender-paired-se">
              <option value="">{form.state ? (filteredSEs.length ? '— none —' : '— No SE in this state —') : '— Pick state first —'}</option>
              {filteredSEs.map((u) => <option key={u.id} value={u.id}>{u.name}{u.state ? ` • ${u.state}` : ''}</option>)}
            </select>
          </Field>
          <Field label="Source URL" aiHint={aiSuggested.has('source_url')}><input value={form.source_url} onChange={(e) => set('source_url', e.target.value)} className="input" data-testid="tender-source-url" /></Field>
          <Field label="Notes" aiHint={aiSuggested.has('source_notes')}><textarea value={form.source_notes} onChange={(e) => set('source_notes', e.target.value)} className="input" rows={2} data-testid="tender-notes" /></Field>
          <Field label="Publish date" aiHint={aiSuggested.has('publish_date')}><input type="date" value={form.publish_date} onChange={(e) => set('publish_date', e.target.value)} className="input" data-testid="tender-publish-date" /></Field>
          <Field label="Submission deadline *" aiHint={aiSuggested.has('submission_deadline')}><input type="date" value={form.submission_deadline} onChange={(e) => set('submission_deadline', e.target.value)} className="input" data-testid="tender-deadline" /></Field>
          <Field label="Opening date" aiHint={aiSuggested.has('opening_date')}><input type="date" value={form.opening_date} onChange={(e) => set('opening_date', e.target.value)} className="input" data-testid="tender-opening" /></Field>
          <Field label="Estimated value (₹)" aiHint={aiSuggested.has('estimated_value')}><input type="number" value={form.estimated_value} onChange={(e) => set('estimated_value', parseFloat(e.target.value) || 0)} className="input" data-testid="tender-est-value" /></Field>
        </div>

        {/* Flags */}
        <div className="px-6 pb-4">
          <h3 className="font-semibold text-[#052A23] mb-3 text-sm uppercase tracking-wider">Tender flags</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <label className="flex items-center gap-2 text-sm px-3 py-2 rounded-lg border border-[#D5E8E4] cursor-pointer hover:bg-[#F7FBFA]">
              <input type="checkbox" checked={form.reverse_auction} onChange={(e) => set('reverse_auction', e.target.checked)} data-testid="tender-reverse-auction" />
              <span>Reverse Auction</span>
            </label>
            <label className="flex items-center gap-2 text-sm px-3 py-2 rounded-lg border border-[#D5E8E4] cursor-pointer hover:bg-[#F7FBFA]">
              <input type="checkbox" checked={form.emd_exemption} onChange={(e) => set('emd_exemption', e.target.checked)} data-testid="tender-emd-exemption" />
              <span>EMD Exemption</span>
            </label>
            <label className="flex items-center gap-2 text-sm px-3 py-2 rounded-lg border border-[#D5E8E4] cursor-pointer hover:bg-[#F7FBFA]">
              <input type="checkbox" checked={form.rate_contract} onChange={(e) => set('rate_contract', e.target.checked)} data-testid="tender-rate-contract" />
              <span>Rate Contract (RC)</span>
            </label>
          </div>
        </div>

        {/* EMD */}
        <div className="px-6 pb-4">
          <h3 className="font-semibold text-[#052A23] mb-2 text-sm uppercase tracking-wider">EMD</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.emd.required} onChange={(e) => setEmd('required', e.target.checked)} data-testid="tender-emd-required" /> EMD Required</label>
            <Field label="Amount (₹)"><input type="number" disabled={!form.emd.required} value={form.emd.amount} onChange={(e) => setEmd('amount', parseFloat(e.target.value) || 0)} className="input" data-testid="tender-emd-amount" /></Field>
            <Field label="Mode"><select disabled={!form.emd.required} value={form.emd.mode} onChange={(e) => setEmd('mode', e.target.value)} className="input" data-testid="tender-emd-mode">{['Demand Draft', 'Bank Guarantee', 'Online', 'Exempt'].map((m) => <option key={m}>{m}</option>)}</select></Field>
            <Field label="Bank"><input disabled={!form.emd.required} value={form.emd.bank} onChange={(e) => setEmd('bank', e.target.value)} className="input" /></Field>
            <Field label="Reference #"><input disabled={!form.emd.required} value={form.emd.ref_number} onChange={(e) => setEmd('ref_number', e.target.value)} className="input" /></Field>
            <Field label="Validity date"><input type="date" disabled={!form.emd.required} value={form.emd.validity_date} onChange={(e) => setEmd('validity_date', e.target.value)} className="input" /></Field>
          </div>
        </div>

        {/* Documents (mandatory) — Tender Document itself is uploaded at the
            top of the modal (iter99b); this section holds the 3 supporting
            docs only. */}
        <div className="px-6 pb-4">
          <h3 className="font-semibold text-[#052A23] mb-1 text-sm uppercase tracking-wider">Supporting Documents <span className="text-rose-600 font-bold">*</span></h3>
          <p className="text-xs text-[#6D8E87] mb-3">The remaining 3 files are also mandatory. PDF / Word / Excel / images. Max 25 MB each.</p>
          <div className="space-y-2">
            {REQUIRED_DOCS.filter((d) => d.key !== 'tender_doc').map((d) => {
              const uploaded = docs[d.key];
              const isUploading = uploadingKey === d.key;
              const isAiPdf = uploaded && uploaded.ai && (uploaded.name || '').toLowerCase().endsWith('.pdf');
              const flagState = flaggedSlots[d.key];
              return (
                <div key={d.key} className={`rounded-lg border ${uploaded ? 'border-emerald-300 bg-emerald-50' : 'border-[#D5E8E4] bg-white'}`} data-testid={`tender-doc-slot-${d.key}`}>
                  <div className="flex items-center gap-3 px-3 py-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-[#052A23]">
                        {d.label} <span className="text-rose-600">*</span>
                        {isAiPdf && uploaded.pages && (
                          <span className="ml-2 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700 uppercase tracking-wide" data-testid={`tender-doc-pages-${d.key}`}>
                            pp {uploaded.pages[0]}{uploaded.pages[1] && uploaded.pages[1] !== uploaded.pages[0] ? `–${uploaded.pages[1]}` : ''}
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-[#6D8E87] truncate">{uploaded ? uploaded.name : d.hint}</div>
                    </div>
                    {uploaded ? (
                      <>
                        <span className="text-xs text-emerald-700 font-semibold" data-testid={`tender-doc-ok-${d.key}`}>
                          {uploaded.ai ? '✨ AI extracted ✓' : 'Uploaded ✓'}
                        </span>
                        <button type="button" onClick={() => removeDoc(d.key)} className="text-xs px-2 py-1 rounded border border-rose-200 text-rose-600 hover:bg-rose-50" data-testid={`tender-doc-remove-${d.key}`}>Remove</button>
                      </>
                    ) : (
                      <label className={`text-xs px-3 py-1.5 rounded font-semibold cursor-pointer ${isUploading ? 'bg-gray-200 text-gray-500' : 'bg-[#00a69c] text-white hover:bg-[#00bfb3]'}`} data-testid={`tender-doc-upload-${d.key}`}>
                        {isUploading ? 'Uploading…' : 'Choose file'}
                        <input type="file" className="hidden" accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.webp" onChange={(e) => handleFile(d.key, e.target.files?.[0])} disabled={isUploading} />
                      </label>
                    )}
                  </div>
                  {/* iter101 — Inline iframe preview + wrong-slice feedback for AI-picked PDFs */}
                  {isAiPdf && (
                    <div className="px-3 pb-3 pt-1 border-t border-emerald-200/60">
                      <iframe
                        src={authedPreviewUrl(uploaded.url)}
                        title={`${d.label} preview`}
                        className="w-full rounded border border-emerald-200 bg-white"
                        style={{ height: 200 }}
                        data-testid={`tender-doc-preview-${d.key}`}
                      />
                      <div className="flex items-center justify-between mt-2 gap-2">
                        <span className="text-[11px] text-[#3B5A54]">
                          AI sliced these pages from the tender PDF. Verify before creating.
                        </span>
                        {flagState === 'reported' ? (
                          <span className="text-[11px] font-semibold text-amber-700" data-testid={`tender-doc-flagged-${d.key}`}>
                            🚩 Reported — please upload the correct file
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => reportWrongSlice(d.key)}
                            disabled={flagState === 'reporting'}
                            className="text-[11px] px-2 py-1 rounded border border-amber-300 text-amber-800 bg-amber-50 hover:bg-amber-100 font-semibold disabled:opacity-60"
                            data-testid={`tender-doc-wrong-slice-${d.key}`}
                          >
                            {flagState === 'reporting' ? 'Reporting…' : '🚩 AI picked the wrong pages'}
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* iter107 — AI Feasibility Auto-Check panel */}
        {feasibilityRows.length > 0 && (
          <div className="px-6 pb-4">
            <FeasibilityAiPanel
              rows={feasibilityRows}
              setRows={setFeasibilityRows}
              tenderDocUrl={docs.tender_doc?.url || ''}
            />
          </div>
        )}

        <div className="p-6 border-t border-[#E2ECEA] flex justify-end gap-2 sticky bottom-0 bg-white">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-[#D5E8E4] text-[#3B5A54] text-sm">Cancel</button>
          <button onClick={submit} disabled={saving || !allDocsUploaded} title={allDocsUploaded ? '' : 'Upload all 4 documents to enable'} className="px-4 py-2 rounded-lg bg-[#00a69c] text-white text-sm font-semibold hover:bg-[#00bfb3] disabled:opacity-60" data-testid="tender-create-submit">
            {saving ? 'Creating…' : 'Create Tender'}
          </button>
        </div>
      </div>
      <style>{`.input{width:100%;padding:8px 12px;border:1px solid #D5E8E4;border-radius:8px;font-size:14px;background:white}.input:disabled{background:#F5F8F7;color:#9DB4AF;cursor:not-allowed}`}</style>
    </div>
  );
}

const Field = ({ label, children, aiHint }) => (
  <label className="block">
    <span className="flex items-center gap-1 text-xs font-medium text-[#3B5A54] mb-1">
      {label}
      {aiHint && (
        <span
          className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-indigo-50 text-indigo-700 rounded-full text-[9px] font-bold uppercase tracking-wide"
          title="Suggested by AI from the tender document. Edit if incorrect."
          data-testid="ai-suggested-badge"
        >✨ AI</span>
      )}
    </span>
    {children}
  </label>
);

// iter107 — Feasibility AI Auto-Check panel
// Shows the AI's cross-check of the tender's eligibility criteria against
// the tenant's Feasibility Profile + Products. Each row is TE-editable
// (verdict dropdown) and has a 🚩 "AI got this wrong" flag button that
// reuses the report-wrong-slice audit pattern.
const _VERDICT_LABELS = {
  yes: { text: '✅ Yes', cls: 'bg-emerald-50 text-emerald-800 border-emerald-200' },
  no: { text: '❌ No', cls: 'bg-rose-50 text-rose-800 border-rose-200' },
  manual_review: { text: '⚠️ Manual review', cls: 'bg-amber-50 text-amber-800 border-amber-200' },
};

function FeasibilityAiPanel({ rows, setRows, tenderDocUrl }) {
  const [flaggedIds, setFlaggedIds] = useState({});

  const counts = useMemo(() => {
    const c = { yes: 0, no: 0, manual_review: 0 };
    for (const r of rows) if (c[r.verdict] !== undefined) c[r.verdict] += 1;
    return c;
  }, [rows]);

  const eligible = counts.no === 0;
  const setVerdict = (id, v) => setRows(rows.map((r) => r.id === id ? { ...r, verdict: v } : r));

  const flagWrong = async (row) => {
    setFlaggedIds((f) => ({ ...f, [row.id]: 'flagging' }));
    try {
      // Reuse the iter101 wrong-slice audit collection — same shape,
      // slot = "feasibility::<category>", note = criterion + AI verdict.
      await axios.post(`${API}/tenders/report-wrong-slice`, {
        slot: `feasibility::${row.category || 'other'}`,
        ai_file_url: '',
        ai_pages: row.page_ref ? [row.page_ref] : null,
        tender_doc_url: tenderDocUrl,
        note: `AI feasibility verdict "${row.verdict}" was wrong for: ${row.criterion} (required=${row.required}, ours=${row.ours})`,
      });
      setFlaggedIds((f) => ({ ...f, [row.id]: 'flagged' }));
    } catch {
      setFlaggedIds((f) => ({ ...f, [row.id]: 'error' }));
    }
  };

  return (
    <div className="rounded-xl border border-indigo-200 bg-indigo-50/40 overflow-hidden" data-testid="feasibility-ai-panel">
      <div className="px-4 py-3 flex items-center justify-between bg-indigo-100/70 border-b border-indigo-200">
        <div>
          <div className="flex items-center gap-2 text-sm font-bold text-indigo-900">
            <span>✨ AI Feasibility Auto-Check</span>
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${eligible ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'}`}
              data-testid="feasibility-overall">
              {eligible ? '🟢 Eligible so far' : `🔴 ${counts.no} blocker(s)`}
            </span>
          </div>
          <p className="text-[11px] text-indigo-900/80 mt-0.5">
            {counts.yes} matched · {counts.no} blocked · {counts.manual_review} need manual review. Edit any verdict below if the AI got it wrong.
          </p>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-white/60 text-[10px] uppercase tracking-wide text-indigo-900/70">
            <tr>
              <th className="text-left px-3 py-2 font-semibold">Criterion</th>
              <th className="text-left px-3 py-2 font-semibold">Required</th>
              <th className="text-left px-3 py-2 font-semibold">Our capability</th>
              <th className="text-left px-3 py-2 font-semibold">AI verdict</th>
              <th className="text-left px-3 py-2 font-semibold">Reasoning</th>
              <th className="text-left px-3 py-2 font-semibold">Pg</th>
              <th className="text-left px-3 py-2 font-semibold"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const flag = flaggedIds[r.id];
              return (
                <tr key={r.id} className="border-t border-indigo-100" data-testid={`feasibility-row-${r.id}`}>
                  <td className="px-3 py-2 text-[#052A23] max-w-[220px]">
                    <div className="font-semibold line-clamp-2">{r.criterion}</div>
                    <div className="text-[9px] uppercase tracking-wide text-indigo-700/70 mt-0.5">{r.category}</div>
                  </td>
                  <td className="px-3 py-2 text-[#3B5A54] max-w-[160px]"><div className="line-clamp-2">{String(r.required || '—')}</div></td>
                  <td className="px-3 py-2 text-[#3B5A54] max-w-[160px]"><div className="line-clamp-2">{String(r.ours || '—')}</div></td>
                  <td className="px-3 py-2">
                    <select value={r.verdict} onChange={(e) => setVerdict(r.id, e.target.value)}
                      className={`text-[11px] px-2 py-1 rounded border font-semibold ${_VERDICT_LABELS[r.verdict]?.cls || ''}`}
                      data-testid={`feasibility-verdict-${r.id}`}>
                      <option value="yes">✅ Yes</option>
                      <option value="no">❌ No</option>
                      <option value="manual_review">⚠️ Manual review</option>
                    </select>
                  </td>
                  <td className="px-3 py-2 text-[#3B5A54] max-w-[220px]"><div className="line-clamp-2">{r.reasoning}</div></td>
                  <td className="px-3 py-2 text-[#6D8E87]">{r.page_ref || '—'}</td>
                  <td className="px-3 py-2">
                    {flag === 'flagged' ? (
                      <span className="text-[10px] text-amber-800 font-semibold" data-testid={`feasibility-flagged-${r.id}`}>🚩 Reported</span>
                    ) : (
                      <button type="button" onClick={() => flagWrong(r)} disabled={flag === 'flagging'}
                        className="text-[10px] px-2 py-1 rounded border border-amber-300 text-amber-800 bg-amber-50 hover:bg-amber-100 disabled:opacity-50 font-semibold"
                        title="Flag this AI verdict as wrong for prompt-tuning"
                        data-testid={`feasibility-flag-${r.id}`}>
                        {flag === 'flagging' ? '…' : '🚩 Wrong?'}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="px-4 py-2 text-[10px] text-indigo-900/70 border-t border-indigo-200">
        Manual review items require the TE's judgement — the AI can't automatically check them. Edit any verdict above before creating the tender.
      </div>
    </div>
  );
}

